<script setup lang="ts">
import { computed } from 'vue'
import ReadinessNonNumericUnit from '@/components/ReadinessReport/ReadinessNonNumericUnit.vue'

const props = withDefaults(defineProps<{
    readinessScore?: number
    mode?: 'numeric' | 'non-numeric' | 'personalized' | 'generic'
}>(), {
    readinessScore: 90,
    mode: 'numeric',
})

const nonNumericFillPct = computed(() => {
    if (props.readinessScore < 40) {
        return 25
    } else if (props.readinessScore < 60) {
        return 50
    } else if (props.readinessScore < 80) {
        return 75
    } else if (props.readinessScore >= 80) {
        return 100
    } else {
        return 0
    }
})
</script>

<template>
    <div class="readiness-scale">
        <div
            v-if="mode === 'numeric'"
            v-dark
            class="readiness-scale__bar"
        >
            <div
                v-dark
                class="readiness-scale__fill"
                :class="{
                    'readiness-scale__fill--full': readinessScore >= 100
                }"
                :style="{
                    'width': `${readinessScore || 0}%`,
                }"
            >
                <img
                    class="readiness-scale__img"
                    src="@/assets/readiness/swirl.png"
                >
            </div>
        </div>
        <div
            v-else
            class="readiness-scale__non-numeric-bars"
        >
            <div class="readiness-scale__non-numeric-mask">
                <div
                    class="readiness-scale__non-numeric-fill"
                    :style="{
                        'width': `${nonNumericFillPct || 0}%`,
                    }"
                >
                    <img
                        class="readiness-scale__non-numeric-img"
                        src="@/assets/readiness/swirl.png"
                    >
                </div>
            </div>
            <ReadinessNonNumericUnit
                v-dark
                class="readiness-scale__non-numeric-unit readiness-scale__non-numeric-unit--low"
                :class="{
                    'readiness-scale__non-numeric-unit--filled': readinessScore > 0,
                }"
            />
            <ReadinessNonNumericUnit
                v-dark
                class="readiness-scale__non-numeric-unit readiness-scale__non-numeric-unit--moderate"
                :class="{
                    'readiness-scale__non-numeric-unit--filled': readinessScore >= 40,
                }"
            />
            <ReadinessNonNumericUnit
                v-dark
                class="readiness-scale__non-numeric-unit readiness-scale__non-numeric-unit--good"
                :class="{
                    'readiness-scale__non-numeric-unit--filled': readinessScore >= 60,
                }"
            />
            <ReadinessNonNumericUnit
                v-dark
                class="readiness-scale__non-numeric-unit readiness-scale__non-numeric-unit--high"
                :class="{
                    'readiness-scale__non-numeric-unit--filled': readinessScore >= 80,
                }"
            />
        </div>
        <div v-dark class="readiness-scale__bottom-labels">
            <div
                class="readiness-scale__low-label"
                :class="{
                    'readiness-scale__low-label--non-numeric': mode !== 'numeric'
                }"
            >
                Low
            </div>
            <div
                class="readiness-scale__moderate-label"
                :class="{
                    'readiness-scale__moderate-label--non-numeric': mode !== 'numeric'
                }"
            >
                Moderate
            </div>
            <div
                class="readiness-scale__good-label"
                :class="{
                    'readiness-scale__good-label--non-numeric': mode !== 'numeric'
                }"
            >
                Good
            </div>
            <div
                class="readiness-scale__high-label"
                :class="{
                    'readiness-scale__high-label--non-numeric': mode !== 'numeric'
                }"
            >
                High
            </div>
        </div>
        <template v-if="mode === 'numeric'">
            <div v-dark class="readiness-scale__left-label">0</div>
            <div v-dark class="readiness-scale__right-label">10</div>
            <div class="readiness-scale__vertical-lines">
                <div v-dark class="readiness-scale__moderate-start-line" />
                <div v-dark class="readiness-scale__good-start-line" />
                <div v-dark class="readiness-scale__high-start-line" />
            </div>
            <div
                v-dark
                class="readiness-scale__marker"
                :style="{
                    'left': `calc(${(readinessScore || 0)}% - 6.5px)`
                }"
            >
                <!-- eslint-disable -->
                <svg xmlns="http://www.w3.org/2000/svg" width="11" height="8" viewBox="0 0 11 8" fill="none">
                    <path d="M4.57779 7.03324L0.223127 1.62732C-0.303868 0.973104 0.161815 0 1.00189 0H9.71122C10.5513 0 11.017 0.973103 10.49 1.62732L6.13531 7.03324C5.73504 7.53015 4.97807 7.53015 4.57779 7.03324Z" fill="currentColor"/>
                </svg>
                <!-- eslint-enable -->
            </div>
        </template>
    </div>
</template>

<style lang="scss" scoped>
.readiness-scale {
    position: relative;

    &__bar {
        background-color: $white;
        height: 14px;
        border: 1px solid $brand-black;
        border-radius: 8px;
        overflow: hidden;

        &--dark {
            background-color: $moonlit-ocean;
            border-color: $jet;
        }
    }

    &__fill {
        overflow: hidden;
        box-sizing: border-box;
        border-right: 1px solid $brand-black;

        &--full {
            border: none;
        }

        &--dark {
            border-color: $jet;
        }
    }

    &__img {
        width: 351px;
        height: 14px;

        @include breakpoint(black-bear) {
            width: 307px;
        }
    }

    &__non-numeric-mask {
        mask-image: url('@/assets/readiness/non-numerical-unit.svg');
        mask-repeat: space;

        @include breakpoint(black-bear) {
            mask-size: 79px;
        }
    }

    &__non-numeric-fill {
        overflow: hidden;
    }

    &__non-numeric-img {
        width: 360px;
        height: 14px;

        @include breakpoint(black-bear) {
            width: 339px;
        }
    }

    &__non-numeric-unit {
        position: absolute;
        top: 0;
        color: $white;

        &--dark {
            color: $jet;
        }

        &--low {
            left: 0;
        }

        &--moderate {
            left: 91.5px;

            @include breakpoint(black-bear) {
                left: 87.1px;
            }
        }

        &--good {
            right: 91.5px;

            @include breakpoint(black-bear) {
                right: 87.1px;
            }
        }

        &--high {
            right: 0;
        }

        &--filled {
            color: transparent;
        }
    }

    &__bottom-labels {
        position: absolute;
        bottom: -20px;
        display: flex;
        color: $ash;
        font-size: 12px;
        font-weight: 500;
        line-height: 16px;

        &--dark {
            color: $fog;
        }
    }

    &__low-label {
        margin-left: 59px;

        @include breakpoint(black-bear) {
            margin-left: 50px
        }

        &--non-numeric {
            margin-left: 31px;

            @include breakpoint(black-bear) {
                margin-left: 28px;
            }
        }
    }

    &__moderate-label {
        margin-left: 66px;

        @include breakpoint(black-bear) {
            margin-left: 53px;
        }

        &--non-numeric {
            margin-left: 53px;

            @include breakpoint(black-bear) {
                margin-left: 49px;
            }
        }
    }

    &__good-label {
        margin-left: 27px;

        @include breakpoint(black-bear) {
            margin-left: 18.5px;
        }

        &--non-numeric {
            margin-left: 48px;

            @include breakpoint(black-bear) {
                margin-left: 43px;
            }
        }
    }

    &__high-label {
        margin-left: 41px;

        @include breakpoint(black-bear) {
            margin-left: 32px;
        }

        &--non-numeric {
            margin-left: 64px;

            @include breakpoint(black-bear) {
                margin-left: 57px;
            }
        }
    }

    &__left-label {
        position: absolute;
        top: 0;
        left: -12px;
        color: $steel;
        font-size: 12px;
        font-weight: 500;
        line-height: 16px;
        letter-spacing: -0.2px;

        &--dark {
            color: $pewter;
        }
    }

    &__right-label {
        position: absolute;
        top: 0;
        right: -18px;
        color: $steel;
        font-size: 12px;
        font-weight: 500;
        line-height: 16px;
        letter-spacing: -0.5px;

        &--dark {
            color: $pewter;
        }
    }

    &__vertical-lines {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        display: flex;
    }

    &__moderate-start-line,
    &__good-start-line,
    &__high-start-line {
        border-left: 1px dashed $brand-black;
        height: 14px;

        &--dark {
            border-color: $jet;
        }
    }

    &__moderate-start-line {
        margin-left: 139.5px;

        @include breakpoint(black-bear) {
            margin-left: 122px;
        }
    }

    &__good-start-line {
        margin-left: 69px;

        @include breakpoint(black-bear) {
            margin-left: 60px;
        }
    }

    &__high-start-line {
        margin-left: 68.5px;

        @include breakpoint(black-bear) {
            margin-left: 60px;
        }
    }

    &__marker {
        color: $brand-black;
        position: absolute;
        top: -14px;
        display: flex;

        &--dark {
            color: $white;
        }
    }
}
</style>
